import {useState, useRef, useCallback, useEffect, useMemo} from 'react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useCustomQuery, useCustomMutation} from '@salesforce/commerce-sdk-react'

/**
 * Hook to fetch address suggestions based on address and postal code.
 */
const useAddressSuggestion = (address1, countryCode) => {
    const [isLoading, setIsLoading] = useState(false)
    const [suggestions, setSuggestions] = useState([])

    const {refetch} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_address1: encodeURIComponent(address1),
                    ...(countryCode && {c_countryCode: countryCode})
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/address-suggestion',
                    apiName: 'address-suggestions'
                }
            },
            rawResponse: false
        },
        {
            enabled: false
        }
    )

    const fetchSuggestions = async ({address, shouldFetch = true}) => {
        if (!shouldFetch || !address) return

        setIsLoading(true)
        try {
            const result = await refetch({
                options: {
                    body: JSON.stringify({
                        address1: address
                    })
                }
            })
            if (!result?.error) {
                setSuggestions(result?.data?.suggestions || [])
            }
        } catch (error) {
            logger.error('Error fetching address suggestions (mock)', {
                namespace: 'useAddressSuggestion',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
    }

    return {
        isLoading,
        suggestions,
        fetchSuggestions
    }
}

const useAddressValidation = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [address, setAddress] = useState({})
    const resolveRef = useRef(null)
    const rejectRef = useRef(null)

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            body: JSON.stringify({
                address1: address.address1,
                address2: address.apartment,
                city: address.city,
                stateCode: address.stateCode,
                countryCode: address.countryCode,
                postalCode: address.postalCode
            }),
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/address-validation',
                apiName: 'address-validation'
            }
        }
    })

    // After a re-render with the new address, fire the mutation
    useEffect(() => {
        if (!resolveRef.current || !address.address1) return

        const run = async () => {
            try {
                const data = await mutation.mutateAsync()
                resolveRef.current(data)
            } catch (error) {
                logger.error('Error fetching address validation (mock)', {
                    namespace: 'useAddressValidation',
                    additionalProperties: {error: error.message, stack: error.stack}
                })
                resolveRef.current(undefined)
            } finally {
                resolveRef.current = null
                rejectRef.current = null
                setIsLoading(false)
            }
        }
        run()
    }, [address])

    const validateAddress = useCallback(({address: addr, shouldFetch = true}) => {
        if (!shouldFetch) return Promise.resolve(undefined)

        setIsLoading(true)
        return new Promise((resolve) => {
            resolveRef.current = resolve
            // Trigger a re-render so the hook config body picks up the new address
            setAddress({...addr})
        })
    }, [])

    return {
        isLoading,
        validateAddress
    }
}

const usePhoneValidation = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [validationResponse, setValidationResponse] = useState(null)
    const [phoneData, setPhoneData] = useState({phoneNumber: '', countryCode: ''})
    const resolveRef = useRef(null)

    const cleanedPhone = useMemo(
        () => phoneData.phoneNumber.replace(/[^\d+]/g, ''),
        [phoneData.phoneNumber]
    )

    const {refetch} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_phoneNumber: cleanedPhone,
                    c_countryCode: phoneData.countryCode
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/data-validation',
                    apiName: 'data-validation'
                }
            },
            rawResponse: false
        },
        {enabled: false}
    )

    useEffect(() => {
        if (!resolveRef.current || !phoneData.phoneNumber) return

        const run = async () => {
            try {
                const result = await refetch()
                const data = result?.data
                setValidationResponse(data)
                resolveRef.current(data)
            } catch (error) {
                logger.error('Error fetching phone validation', {
                    namespace: 'usePhoneValidation',
                    additionalProperties: {error: error.message, stack: error.stack}
                })
                resolveRef.current(null)
            } finally {
                resolveRef.current = null
                setIsLoading(false)
            }
        }
        run()
    }, [phoneData])

    const fetchPhoneValidation = useCallback(({phoneNumber, countryCode, shouldFetch = true}) => {
        if (!phoneNumber || !shouldFetch) return Promise.resolve(null)

        setIsLoading(true)
        return new Promise((resolve) => {
            resolveRef.current = resolve
            setPhoneData({phoneNumber, countryCode})
        })
    }, [])

    return {isLoading, fetchPhoneValidation, validationResponse}
}

const useEmailValidation = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [validationResponse, setValidationResponse] = useState(null)
    const [emailData, setEmail] = useState({email: ''})
    const resolveRef = useRef(null)

    const {refetch} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_email: emailData.email
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/data-validation',
                    apiName: 'data-validation'
                }
            },
            rawResponse: false
        },
        {enabled: false}
    )

    useEffect(() => {
        if (!resolveRef.current || !emailData.email) return

        const run = async () => {
            try {
                const result = await refetch()
                const data = result?.data

                setValidationResponse(data)
                resolveRef.current(data)
            } catch (error) {
                logger.error('Error fetching email validation', {
                    namespace: 'useEmailValidation',
                    additionalProperties: {
                        error: error.message,
                        stack: error.stack
                    }
                })

                resolveRef.current(null)
            } finally {
                resolveRef.current = null
                setIsLoading(false)
            }
        }

        run()
    }, [emailData])

    const fetchEmailValidation = useCallback(({email, shouldFetch = true}) => {
        if (!email || !shouldFetch) return Promise.resolve(null)

        setIsLoading(true)

        return new Promise((resolve) => {
            resolveRef.current = resolve
            setEmail({ email })
        })
    }, [])

    return {
        isLoading,
        fetchEmailValidation,
        validationResponse
    }
}

export {useAddressSuggestion, useAddressValidation, usePhoneValidation, useEmailValidation}
